import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Camera, Upload, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_CATEGORIES = [
  "Children",
  "Home & Garden",
  "Pets",
  "Professional Services",
  "Wellness & Beauty",
  "Women's Lifestyle"
];

export default function RegisterPage() {
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    axios.get(`${API}/areas`).then(res => setAreas(res.data)).catch(() => {});
  }, []);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    area: '',
    business_name: '',
    business_category: '',
    business_description: '',
    phone: '',
    website: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    tiktok: '',
    profile_image: '',
    business_logo: '',
    accept_terms: false,
    newsletter_consent: false,
    popia_consent: false,
    media_consent: true
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [profilePreview, setProfilePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({ ...formData, [name]: checked });
  };

  const handleImageUpload = async (file, type) => {
    if (!file || !file.type.startsWith('image/')) return;
    const setter = type === 'profile' ? setUploadingProfile : setUploadingLogo;
    const previewSetter = type === 'profile' ? setProfilePreview : setLogoPreview;
    setter(true);
    previewSetter(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/register-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        [type === 'profile' ? 'profile_image' : 'business_logo']: res.data.image_url
      }));
    } catch (err) {
      alert('Failed to upload image. Please try again.');
      previewSetter(null);
    } finally {
      setter(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required consents
    if (!formData.accept_terms) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    if (!formData.popia_consent) {
      toast.error('Please accept the POPIA consent to continue');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('Application submitted! You will be notified once approved.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-6 bg-[#f8f9fa]" data-testid="register-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#2d3748] mb-2">
              Apply for Membership
            </h1>
            <p className="text-[#64748b]">Join our community of women entrepreneurs</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="full_name" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-fullname-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Password *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl pr-12"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="area" className="text-sm font-semibold text-gray-700 mb-2 block">
                Preferred Home Chapter *
              </Label>
              <Select value={formData.area} onValueChange={(value) => setFormData({ ...formData, area: value })} required>
                <SelectTrigger className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl" data-testid="register-area-select">
                  <SelectValue placeholder="Select your preferred home chapter" />
                </SelectTrigger>
                <SelectContent>
                  {areas.map((area) => (
                    <SelectItem key={area} value={area}>
                      {area}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Business Information */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h2 className="text-lg font-semibold text-[#2d3748] mb-4">Business Information</h2>
            </div>

            {/* Profile Photo & Business Logo */}
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Your Profile Photo</Label>
                <div className="relative mx-auto w-28 h-28 rounded-full overflow-hidden bg-gray-100 mb-3 border-2 border-dashed border-gray-300">
                  {profilePreview || formData.profile_image ? (
                    <img src={profilePreview || formData.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Camera className="w-8 h-8 mb-1" />
                      <span className="text-xs">Add photo</span>
                    </div>
                  )}
                </div>
                <label className="cursor-pointer">
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#5b7f95] text-white text-sm rounded-full hover:bg-[#4a6b80] transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploadingProfile ? 'Uploading...' : 'Upload Photo'}
                  </div>
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'profile')} className="hidden" disabled={uploadingProfile} />
                </label>
              </div>
              <div className="text-center">
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Business Logo</Label>
                <div className="relative mx-auto w-28 h-28 rounded-xl overflow-hidden bg-gray-100 mb-3 border-2 border-dashed border-gray-300">
                  {logoPreview || formData.business_logo ? (
                    <img src={logoPreview || formData.business_logo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Upload className="w-8 h-8 mb-1" />
                      <span className="text-xs">Add logo</span>
                    </div>
                  )}
                </div>
                <label className="cursor-pointer">
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#cb9944] text-white text-sm rounded-full hover:bg-[#b08435] transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </div>
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'logo')} className="hidden" disabled={uploadingLogo} />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="business_name" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Business Name <span className="text-gray-500 font-normal">(leave blank if no business yet)</span>
                </Label>
                <Input
                  id="business_name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-business-name-input"
                />
              </div>

              <div>
                <Label htmlFor="business_category" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Business Category
                </Label>
                <Select value={formData.business_category} onValueChange={(value) => setFormData({ ...formData, business_category: value })}>
                  <SelectTrigger className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl" data-testid="register-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="website" className="text-sm font-semibold text-gray-700 mb-2 block">
                Website
              </Label>
              <Input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.example.com"
                className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                data-testid="register-website-input"
              />
            </div>

            <div>
              <Label htmlFor="business_description" className="text-sm font-semibold text-gray-700 mb-2 block">
                Business Description
              </Label>
              <Textarea
                id="business_description"
                name="business_description"
                value={formData.business_description}
                onChange={handleChange}
                rows={4}
                className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                data-testid="register-business-desc-input"
              />
            </div>

            {/* Social Media */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h2 className="text-lg font-semibold text-[#2d3748] mb-4">Social Media Handles</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="facebook" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Facebook
                </Label>
                <Input
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  placeholder="@yourpage or URL"
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-facebook-input"
                />
              </div>

              <div>
                <Label htmlFor="instagram" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Instagram
                </Label>
                <Input
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="@yourhandle"
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-instagram-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="linkedin" className="text-sm font-semibold text-gray-700 mb-2 block">
                  LinkedIn
                </Label>
                <Input
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  placeholder="Profile URL or handle"
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-linkedin-input"
                />
              </div>

              <div>
                <Label htmlFor="tiktok" className="text-sm font-semibold text-gray-700 mb-2 block">
                  TikTok
                </Label>
                <Input
                  id="tiktok"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  placeholder="@yourhandle"
                  className="bg-gray-50 border border-gray-200 focus:border-[#5b7f95] rounded-xl"
                  data-testid="register-tiktok-input"
                />
              </div>
            </div>

            {/* Consent Section */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h2 className="text-lg font-semibold text-[#2d3748] mb-4">Terms & Consent</h2>
            </div>

            {/* Terms and Conditions */}
            <div className="bg-gray-50 p-4 rounded-xl space-y-4">
              <p className="text-sm text-[#2d3748] font-medium">
                I have read and understand the Ts and Cs of being an MBN member. I understand that my business will be listed on the MBN Directory for as long as I am a member. I understand that this service is FREE.* <span className="text-red-500">*</span>
              </p>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="accept_terms"
                  checked={formData.accept_terms}
                  onCheckedChange={(checked) => handleCheckboxChange('accept_terms', checked)}
                  data-testid="register-terms-checkbox"
                />
                <Label htmlFor="accept_terms" className="text-sm text-gray-700 cursor-pointer">
                  I understand and accept these terms.
                </Label>
              </div>
            </div>

            {/* Email Communications */}
            <div className="bg-gray-50 p-4 rounded-xl space-y-4">
              <p className="text-sm text-[#2d3748]">
                By submitting this application, I consent to MBN contacting me via email for occasional updates and communications. I understand that my email will not be used for spam or unsolicited messages. I can opt out of communications at any time.
              </p>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="newsletter_consent"
                  checked={formData.newsletter_consent}
                  onCheckedChange={(checked) => handleCheckboxChange('newsletter_consent', checked)}
                  data-testid="register-newsletter-checkbox"
                />
                <Label htmlFor="newsletter_consent" className="text-sm text-gray-700 cursor-pointer">
                  Yes I would like to receive the monthly newsletter
                </Label>
              </div>
            </div>

            {/* POPIA Consent */}
            <div className="bg-gray-50 p-4 rounded-xl space-y-4">
              <p className="text-sm text-[#2d3748] font-medium">
                POPIA Consent: By completing and submitting this form I consent to MBN collecting, processing and storing my personal information in accordance with POPIA and the MBN Privacy Policy. <span className="text-red-500">*</span>
              </p>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="popia_consent"
                  checked={formData.popia_consent}
                  onCheckedChange={(checked) => handleCheckboxChange('popia_consent', checked)}
                  data-testid="register-popia-checkbox"
                />
                <Label htmlFor="popia_consent" className="text-sm text-gray-700 cursor-pointer">
                  I have read the MBN Privacy Policy and I understand and consent.
                </Label>
              </div>
            </div>

            {/* Media Consent */}
            <div className="bg-gray-50 p-4 rounded-xl space-y-4">
              <p className="text-sm text-[#2d3748]">
                <span className="font-medium">Media Consent (optional)</span> - From time to time MBN use images and videos taken at meetings and events on the social media pages. We will assume by default that you are happy with this. If you DO NOT want your images used please click the box below.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="media_no_consent"
                    checked={!formData.media_consent}
                    onCheckedChange={(checked) => handleCheckboxChange('media_consent', !checked)}
                    data-testid="register-media-no-checkbox"
                  />
                  <Label htmlFor="media_no_consent" className="text-sm text-gray-700 cursor-pointer">
                    "I do not consent to photos or videos of me being used on MBN's social media or communication platforms."
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="media_consent"
                    checked={formData.media_consent}
                    onCheckedChange={(checked) => handleCheckboxChange('media_consent', checked)}
                    data-testid="register-media-yes-checkbox"
                  />
                  <Label htmlFor="media_consent" className="text-sm text-gray-700 cursor-pointer">
                    I do consent (by default)
                  </Label>
                </div>
              </div>
            </div>

            {/* Policy Links */}
            <div className="text-sm text-[#64748b] space-y-2">
              <p>
                See the MBN Privacy Policy{' '}
                <a 
                  href="https://www.momsbiznetwork.co.za/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#5b7f95] hover:text-[#4a6b80] underline"
                >
                  here
                </a>.
              </p>
              <p className="text-xs">
                *MBN reserve the right to change terms with 30 days notice
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-6 font-medium shadow-sm"
              data-testid="register-submit-btn"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#64748b]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#5b7f95] hover:text-[#4a6b80] font-semibold" data-testid="login-link">
                Log In
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-[#64748b] hover:text-[#5b7f95]" data-testid="back-home-link">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
